import {
  FloatingPortal,
  autoUpdate,
  shift,
  useFloating
} from '@floating-ui/react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Fragment } from 'react';
import { AdminTabGroup } from 'types/admin-tabs';
import { adminTabGroupLabel } from 'utils/admin-tabs';

type Props = Readonly<{
  group: AdminTabGroup;
  activeTabIndex: number;
  onSelectTab: (index: number) => void;
}>;

export default function AdminNavGroupMenu({
  group,
  activeTabIndex,
  onSelectTab
}: Props) {
  const isActiveGroup = group.tabs.some((tab) => tab.index === activeTabIndex);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    transform: false,
    whileElementsMounted: autoUpdate,
    middleware: [shift({ padding: 8 })]
  });

  return (
    <Menu as='div' className='h-12'>
      <Menu.Button
        ref={refs.setReference}
        className={clsx(
          'flex h-full items-center gap-1.5 whitespace-nowrap px-4 font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500',
          isActiveGroup
            ? 'border-b-2 border-blue-500 bg-blue-50 text-blue-600'
            : 'border-b border-gray-200 text-gray-700 hover:bg-gray-50'
        )}
      >
        {adminTabGroupLabel(group, activeTabIndex)}
        <ChevronDownIcon className='h-4 w-4' />
      </Menu.Button>
      <FloatingPortal>
        <Transition
          as={Fragment}
          enter='transition ease-out duration-100'
          enterFrom='transform opacity-0 scale-95'
          enterTo='transform opacity-100 scale-100'
          leave='transition ease-in duration-75'
          leaveFrom='transform opacity-100 scale-100'
          leaveTo='transform opacity-0 scale-95'
        >
          <Menu.Items
            ref={refs.setFloating}
            style={floatingStyles}
            className='z-50 w-56 origin-top-left rounded-b-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none'
          >
            {group.tabs.map((tab) => (
              <Menu.Item key={tab.index}>
                {({ active }) => (
                  <button
                    type='button'
                    className={clsx(
                      'w-full px-4 py-2.5 text-left text-sm',
                      tab.index === activeTabIndex &&
                        'bg-blue-50 font-semibold text-blue-600',
                      tab.index !== activeTabIndex &&
                        active &&
                        'bg-gray-50 text-gray-900',
                      tab.index !== activeTabIndex && !active && 'text-gray-700'
                    )}
                    onClick={() => onSelectTab(tab.index)}
                  >
                    {tab.name}
                  </button>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </FloatingPortal>
    </Menu>
  );
}
